import { Box, Flex, Spacer, Wrap, WrapItem } from '@chakra-ui/react';
import { QueryWrapperNext } from '@diamond/shared/data-access';
import { ProductCategory, ProductSubCategory } from '@diamond/shared/types';
import { Button, Divider, Text } from '@diamond/shared/ui';
import { useAuthStore } from '@diamond/sol-buyer-context';
import { useState } from 'react';

type Category2DataProps = ProductSubCategory['data'][0];

export interface BrowseProductFilterProps {
  productCategory: ProductCategory[];
  filterParamByCategory1: string;
  filterParamByCategory2: string;
  filterParamByCategory3: string;
  keyword: string;
  warehouseId: string;
  totalProductCount: number;
  divisiCode: string;
  storageCode: string;
  onSubmit: (
    category: string,
    categoryLv2: string,
    categoryLv3: string
  ) => void;
}

type ItemsFilterable = Pick<Category2DataProps, 'product_count'>;

export function BrowseProductFilter(props: BrowseProductFilterProps) {
  const session = useAuthStore();
  const query = QueryWrapperNext('/product/sub-category', session.access_token);
  const {
    productCategory,
    filterParamByCategory1,
    filterParamByCategory2,
    filterParamByCategory3,
    onSubmit,
  } = props;

  const newProductCategory = [
    {
      id: '',
      name: 'Semua',
      product_count: props.totalProductCount,
    },
    ...productCategory,
  ];

  const getItemsByFilter = <T extends ItemsFilterable>(
    obj: T[],
    key: keyof T,
    filter: string
  ) => {
    return obj
      .filter((item) => item?.product_count > 0)
      .find((v) => v[key] === filter);
  };

  // Set initial state, check from query params
  const [cat1, setCat1] = useState(filterParamByCategory1);
  const [cat2, setCat2] = useState(filterParamByCategory2);
  const [cat3, setCat3] = useState(filterParamByCategory3);

  const { data: subCategory } = query.useQueryGetById<ProductSubCategory>(
    cat1,
    'ProductSubCategory',
    {
      search_query: props.keyword,
      warehouse: props.warehouseId,
      storage_code: props.storageCode,
      divisi_code: props.divisiCode,
    }
  );

  const initialCat2Data =
    subCategory && cat1
      ? getItemsByFilter<Category2DataProps>(
          subCategory.data,
          'category_lv2',
          cat2
        )
      : undefined;

  const [cat2Data, setCat2Data] = useState<Category2DataProps | undefined>(
    initialCat2Data
  );

  const handleResetFilterCat2 = () => {
    setCat2Data(undefined);
    setCat2('');
    setCat3('');
  };

  const handleResetAllFilters = () => {
    handleResetFilterCat2();
    setCat1('');
  };

  const handleSubmit = () => {
    onSubmit(cat1, cat2, cat3);
  };

  return (
    <Box flex="1" mb="6">
      <Divider border="2px" mb="6" />
      <Flex mb="4">
        <Text fontSize="xl" fontWeight="semibold">
          Kategori
        </Text>
        <Spacer />
        <Button
          color="red"
          variant="link"
          border="none"
          fontWeight="normal"
          onClick={handleResetAllFilters}
        >
          Hapus
        </Button>
      </Flex>
      <Wrap>
        {newProductCategory.map((val, idx) => {
          return (
            <WrapItem key={idx}>
              <Button
                bg={cat1 === val.id ? 'rgba(0, 0, 163, 0.1)' : 'white'}
                color={cat1 === val.id ? 'blue' : 'gray.75'}
                borderColor={cat1 === val.id ? 'blue' : 'gray.75'}
                size={{ base: 'sm', md: 'md' }}
                onClick={() => {
                  setCat1(val.id);
                  handleResetFilterCat2();
                }}
              >
                <span>
                  {val.name} ({val.product_count})
                </span>
              </Button>
            </WrapItem>
          );
        })}
      </Wrap>
      {cat1 && (
        <>
          <Divider border="2px" my="6" />
          <Flex mb="4">
            <Text fontSize="lg" fontWeight="semibold">
              {
                getItemsByFilter<ProductCategory>(productCategory, 'id', cat1)
                  ?.name
              }
            </Text>
            <Spacer />
            <Button
              color="red"
              variant="link"
              border="none"
              fontWeight="normal"
              onClick={handleResetFilterCat2}
            >
              Hapus
            </Button>
          </Flex>
          <Wrap>
            {subCategory?.data
              .filter((item) => item.product_count > 0)
              .map((val, idx) => {
                const isActive = cat2 === val.category_lv2;
                return (
                  <WrapItem key={idx}>
                    <Button
                      bg={isActive ? 'rgba(0, 0, 163, 0.1)' : 'white'}
                      color={isActive ? 'blue' : 'gray.75'}
                      borderColor={isActive ? 'blue' : 'gray.75'}
                      size={{ base: 'sm', md: 'md' }}
                      onClick={() => {
                        setCat2Data(val);
                        setCat2(val.category_lv2);
                      }}
                    >
                      {val.name} ({val.product_count})
                    </Button>
                  </WrapItem>
                );
              })}
          </Wrap>
        </>
      )}
      {cat2 && (
        <>
          <Divider border="2px" my="6" />
          <Flex mb="4">
            <Text fontSize="lg" fontWeight="semibold">
              {cat2Data?.name}
            </Text>
            <Spacer />
            <Button
              color="red"
              variant="link"
              border="none"
              fontWeight="normal"
              onClick={() => setCat3('')}
            >
              Hapus
            </Button>
          </Flex>
          <Wrap>
            {cat2Data?.third_category
              ?.filter((item) => item.product_count > 0)
              .map((val, idx) => {
                const isActive = cat3 === val.category_lv3;
                return (
                  <WrapItem key={idx}>
                    <Button
                      bg={isActive ? 'rgba(0, 0, 163, 0.1)' : 'white'}
                      color={isActive ? 'blue' : 'gray.75'}
                      borderColor={isActive ? 'blue' : 'gray.75'}
                      size={{ base: 'sm', md: 'md' }}
                      onClick={() => setCat3(val.category_lv3)}
                    >
                      <span>
                        {val.name} ({val.product_count})
                      </span>
                    </Button>
                  </WrapItem>
                );
              })}
          </Wrap>
        </>
      )}

      <Box flex="1" pt="6">
        <Button w="full" variant="solid" onClick={handleSubmit}>
          Terapkan
        </Button>
      </Box>
    </Box>
  );
}

export default BrowseProductFilter;
